import { useEffect } from "react";

// Global counter of dirty forms across the app. Used by the PWA update
// prompt (and beforeunload) to warn the doctor before losing unsaved work.
let dirtyCount = 0;
const listeners = new Set<(n: number) => void>();

function setCount(n: number) {
  dirtyCount = n;
  listeners.forEach((l) => l(n));
}

export function getUnsavedCount() {
  return dirtyCount;
}

export function subscribeUnsaved(fn: (n: number) => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Mark the current view as having unsaved changes while `dirty` is true.
 * Automatically decrements on unmount or when `dirty` flips false.
 * Also installs a `beforeunload` guard so refresh / tab-close prompts natively.
 */
export function useUnsavedChanges(dirty: boolean) {
  useEffect(() => {
    if (!dirty) return;
    setCount(dirtyCount + 1);
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => {
      setCount(Math.max(0, dirtyCount - 1));
      window.removeEventListener("beforeunload", handler);
    };
  }, [dirty]);
}
